import React, { useRef, useState, useCallback, useLayoutEffect } from "react";
import * as d3 from "d3";
import { GeneticWheelProps, WheelNode } from "../../../../types/geneCodes/geneticWheel";
import { getNodeColor, fitArcLabel, applyCentreTitle, CENTRE_TITLE_FIT, fontForChars, labelSpacingRadius, labelWidth, truncateLabel, charsFor, MIN_LABEL_FONT_PX, motionDuration, MOTION_MS } from "../../../../utils/geneCodes/wheelUtils";
import { processGeneticResults, filterSnpsWithGeneticData, getSnpRiskStatus, getSnpNumericRiskFactor, ProcessedGeneticResult } from "../../../../utils/geneCodes/geneticAnalysisUtils";
import { calculateNumericRiskScore, NumericRiskCounts } from "../../../../utils/geneCodes/riskScoringUtils";
import { matchedInterventions, applyModifier, rollupModifiedCount } from "../../../../utils/geneCodes/interventionOverlay";
import { geneRingForTrait } from "../../../../utils/geneCodes/wheelGeneRing";
import { WheelNodeKind, childKindOf, kindLabel } from "../../../../utils/geneCodes/wheelAuthoring";
import { GeneStage } from "../../../../types/geneCodes/GeneStageTypes";
import { WheelGeneRef } from "../../../../types/geneCodes/Panel";
import { useMeasuredBox } from "./useMeasuredBox";
import "./wheels.css";

type HierarchyRectNode = d3.HierarchyRectangularNode<WheelNode>;

// Module constant so the default does not change identity on every render and rebuild the wheel.
const NO_INTERVENTIONS: string[] = [];

// Keeps a label off the outer edge of the ring.
const LABEL_EDGE_PAD = 14;

const MIN_WHEEL_PX = 260;
const WHEEL_INSET = 24;

// Keeps the ring, its stroke AND the intervention marker outside it clear of the viewBox edge.
const WHEEL_MARGIN = 14;

// Fallback start radius, used only until a ring has been planned.
const LABEL_RING_FRACTION = 0.4;

// A label needs at least this much radial run to say anything useful, however cramped the ring.
const MIN_RUN_PX = 46;

// Every label ends this far inside the rim — ~1cm at 96dpi. A FIXED inset on purpose: the radius used
// to carry the stagger offset, so it moved by a whole label width when the ring's plan changed.
const LABEL_RIM_GAP = 38;

const CENTER_FONT_PX = 14;

// The intervention ring sits this far OUTSIDE the rim — 0.25cm at 96dpi — and is drawn this thin.
const MODIFIER_RIM_GAP = 9;
const MODIFIER_RING_PX = 3;

// The dot marking a modified node, and how far inside the node's outer/trailing corner it sits.
const MODIFIER_DOT_PX = 3;
const MODIFIER_DOT_EDGE = 10;

// A 20-trait panel starves its smallest arcs; every child gets at least this much of the circle.
const MIN_SEGMENT_ANGLE = (6 * Math.PI) / 180;

// The slice the build-mode add wedge takes out of the ring. Zero in view mode, so the allocation is unchanged there.
const ADD_WEDGE_ANGLE = (18 * Math.PI) / 180;

// What a click on the add wedge would create, given the ring currently on screen. A gene is not a
// record — it arrives with the first SNP naming it — so a gene ring adds a SNP, and a SNP ring adds nothing.
// One rule for what the + adds — childKindOf, the same one the inspector reads. A SNP has no child,
// so it gets no wedge; the trait ring's child is a GENE, which is what this used to get wrong.
const addWedgeKind = (type: string) => childKindOf(type as WheelNodeKind);

// Floors every child at MIN_SEGMENT_ANGLE and shares the rest by value, always summing to 2*PI - reserve.
const allocateAngles = (nodes: HierarchyRectNode[], reserve: number = 0) => {
  const count = nodes.length;
  if (count === 0) return;

  const full = 2 * Math.PI - reserve;
  const floor = Math.min(MIN_SEGMENT_ANGLE, full / count);
  const values = nodes.map((node) => Math.max(node.value || 0, 0));
  const total = values.reduce((sum, value) => sum + value, 0);
  const surplus = full - floor * count;

  let cursor = reserve / 2;
  nodes.forEach((node, index) => {
    node.x0 = cursor;
    cursor += floor + surplus * (total > 0 ? values[index] / total : 1 / count);
    node.x1 = cursor;
  });
  nodes[count - 1].x1 = full + reserve / 2;
};

// The live d3 hierarchy keeps references to the WheelNodes built at init, so a rebuild has to be
// copied onto them in place or the repaint shows the previous colours. Matched per parent because
// a gene id repeats across traits.
const syncWheelNodes = (live: WheelNode, fresh: WheelNode) => {
  live.riskLevel = fresh.riskLevel;
  live.modifiedBy = fresh.modifiedBy;
  live.modifiedCount = fresh.modifiedCount;

  if (!live.children || !fresh.children) return;

  const freshById = new Map<string, WheelNode>();
  fresh.children.forEach((child) => freshById.set(child.id, child));
  live.children.forEach((child) => {
    const match = freshById.get(child.id);
    if (match) syncWheelNodes(child, match);
  });
};

const ZoomableSunburst: React.FC<GeneticWheelProps> = ({ selectedPanel, traits = [], snps = [], geneticResults = [], onTraitClick, onGeneClick, onSnpClick, width: widthProp = 800, height: heightProp = 800, ink = "dark", modifierMap, selectedInterventions = NO_INTERVENTIONS, onAddNode, onNavigateUp, hideUngenotyped = false }) => {
  const { ref: boxRef, box } = useMeasuredBox();
  // Held in a ref so a shell that re-creates the handler each render cannot re-run the whole repaint.
  const addNodeRef = useRef(onAddNode);
  addNodeRef.current = onAddNode;
  const addWedge = Boolean(onAddNode);
  // The props are the shell's reserve arithmetic; the measured stage is the box the wheel really has.
  const size = Math.max(MIN_WHEEL_PX, Math.min(box.width || widthProp, box.height || heightProp) - WHEEL_INSET);
  const width = size;
  const height = size;
  // ONE outer radius for the arc, the label run, the modifier band and the centre — they used to drift apart.
  const outerRadius = size / 2 - WHEEL_MARGIN;
  const svgRef = useRef<SVGSVGElement>(null);
  // Position of each node within its ring — only the parity matters, for the stagger.
  const ringIndexRef = useRef<Map<string, number>>(new Map());
  const labelPlanRef = useRef<{ radius: number; whole: boolean; fontSize: number }>({ radius: 0, whole: false, fontSize: MIN_LABEL_FONT_PX });
  const [activeNode, setActiveNode] = useState<WheelNode | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ name: string; node: HierarchyRectNode }>>([]);
  // The centre circle is bound inside a memoised renderSunburst, so it captured the FIRST render's
  // handler — with empty breadcrumbs, which sent every back click to the root instead of one level up.
  const backClickRef = useRef<() => void>(() => undefined);

  const d3Context = useRef<{
    root: HierarchyRectNode | null;
    current: HierarchyRectNode | null;
    container: d3.Selection<SVGGElement, unknown, null, undefined> | null;
    initialized: boolean;
    arc: d3.Arc<any, HierarchyRectNode> | null;
    centerRadius: number;
    outerRadius: number;
    renderedId: string | null;
  }>({
    root: null,
    current: null,
    container: null,
    initialized: false,
    arc: null,
    centerRadius: 0,
    outerRadius: 0,
    renderedId: null,
  });

  const buildWheelData = useCallback(() => {
    if (!selectedPanel) return null;

    const rootNode: WheelNode = {
      id: `panel-${selectedPanel.id_panels}`,
      name: selectedPanel.name_panel,
      description: selectedPanel.desc_short || undefined,
      type: "panel",
      value: 1,
      data: selectedPanel,
      children: [],
    };

    // Process genetic results if available
    const hasGeneticData = geneticResults && geneticResults.length > 0;
    let processedGeneticResults: Map<string, ProcessedGeneticResult> = new Map();

    if (hasGeneticData) {
      processedGeneticResults = processGeneticResults(geneticResults as GeneStage[], snps);
    }

    // Filter SNPs based on genetic data availability
    const filteredSnps = filterSnpsWithGeneticData(snps, processedGeneticResults, hasGeneticData, hideUngenotyped);

    // Group filtered SNPs by trait
    const snpsByTrait: Record<string, typeof filteredSnps> = {};

    // Initialize trait map
    traits.forEach((trait) => {
      snpsByTrait[trait.trait_name] = [];
    });

    filteredSnps.forEach((snp) => {
      if (snp.subcategory && snpsByTrait[snp.subcategory]) {
        snpsByTrait[snp.subcategory].push(snp);
      }
    });

    // Add traits as children
    traits.forEach((trait) => {
      const traitSnps = snpsByTrait[trait.trait_name] || [];

      // Skip traits with no SNPs if genetic data is provided
      if (hasGeneticData && traitSnps.length === 0) {
        return;
      }

      const traitNode: WheelNode = {
        id: `trait-${trait.idtraits}`,
        name: trait.trait_name,
        description: trait.trait_desc || undefined,
        type: "trait",
        value: traitSnps.length || 1,
        data: trait,
        children: [],
        hiddenReason: trait.hidden_reason ?? null,
      };

      // Track numeric risk factors for trait calculation (legacy approach)
      const traitNumericCounts: NumericRiskCounts = { total: 0, sum: 0, count: 0 };

      // The ONE ring derivation (wheelGeneRing.ts) — server ids key the nodes, names are the fallback.
      // R6's SNP-less nodes join the ring, but NOT once the SNPs are filtered to a patient's results:
      // there an unmatched gene row means no data for it, and an empty wedge would claim otherwise.
      geneRingForTrait(trait, traitSnps, !hasGeneticData).forEach((ring) => {
        const geneSnps = ring.snps;

        const geneNode: WheelNode = {
          id: ring.nodeId,
          name: ring.name,
          type: "gene",
          value: geneSnps.length || 1,
          data: { gene: ring.name, wheelGeneId: ring.wheelGeneId, registryRef: ring.registryRef },
          children: [],
          // A row-less gene is derived from visible SNPs only, so it has no reason of its own.
          hiddenReason: ring.wheelGeneId != null ? trait.genes?.find((row: WheelGeneRef) => row.id === ring.wheelGeneId)?.hidden_reason ?? null : null,
        };

        // Calculate gene risk level using legacy numeric approach
        const geneNumericCounts: NumericRiskCounts = { total: 0, sum: 0, count: 0 };

        // Add SNPs as children of the gene
        geneSnps.forEach((snp) => {
          const riskStatus = hasGeneticData ? getSnpRiskStatus(snp, processedGeneticResults) : null;
          const numericRiskFactor = hasGeneticData ? getSnpNumericRiskFactor(snp, processedGeneticResults) : 0;

          const matched = matchedInterventions(snp.rs, modifierMap as Map<string, string[]>, selectedInterventions);

          const snpNode: WheelNode = {
            id: `snp-${snp.rs}`,
            name: snp.rs || "",
            type: "snp",
            value: 1,
            data: snp,
            riskLevel: riskStatus,
            modifiedBy: matched,
            hiddenReason: snp.hidden_reason ?? null,
          };

          geneNode.children?.push(snpNode);

          // Accumulate numeric risk factors for gene calculation (legacy approach)
          if (hasGeneticData && numericRiskFactor > 0) {
            geneNumericCounts.sum += applyModifier(numericRiskFactor, matched.length > 0);
            geneNumericCounts.count += 1;
            geneNumericCounts.total += 1;
          }
        });

        // Calculate gene risk level using legacy approach (average of SNP numeric risk factors)
        if (hasGeneticData) {
          geneNode.riskLevel = calculateNumericRiskScore(geneNumericCounts, false); // false = gene level

          // Add gene's average risk factor to trait calculation
          if (geneNumericCounts.count > 0) {
            const geneAverageRisk = geneNumericCounts.sum / geneNumericCounts.count;
            traitNumericCounts.sum += geneAverageRisk;
            traitNumericCounts.count += 1;
            traitNumericCounts.total += 1;
          }
        }

        traitNode.children?.push(geneNode);
      });

      // Calculate trait risk level using legacy approach (average of gene risk factors)
      if (hasGeneticData) {
        traitNode.riskLevel = calculateNumericRiskScore(traitNumericCounts, true); // true = trait level
      }

      // Only add trait if it has children or if no genetic data is provided
      if (traitNode.children && (traitNode.children.length > 0 || !hasGeneticData)) {
        rootNode.children?.push(traitNode);
      }
    });

    rollupModifiedCount(rootNode);

    return rootNode;
  }, [selectedPanel, traits, snps, geneticResults, modifierMap, selectedInterventions, hideUngenotyped]);

  // The arc generator draws every ring between the same two radii, so the label ring is one fraction of it.
  // One ring is on screen at a time, so the whole ring is planned together: the largest font at which
  // the LONGEST name still fits, and a start radius pulled inward far enough to hold it whole.
  const planRing = useCallback(
    (nodes: HierarchyRectNode[]) => {
      const live = d3Context.current.outerRadius || outerRadius;
      const hub = live * 0.2;
      const maxRun = Math.max(0, live - hub - LABEL_EDGE_PAD - LABEL_RIM_GAP);
      const longest = nodes.reduce((most, d) => Math.max(most, (d.data.name || "").length), 0);
      const narrowest = nodes.reduce((least, d) => Math.min(least, d.x1 - d.x0), Number.POSITIVE_INFINITY);
      if (!longest) return { radius: hub + maxRun * LABEL_RING_FRACTION, whole: false, fontSize: MIN_LABEL_FONT_PX };

      for (const fontSize of [14, 12, 11, MIN_LABEL_FONT_PX]) {
        const need = labelWidth(fontSize, longest);
        const textRadius = live - LABEL_EDGE_PAD - LABEL_RIM_GAP - need;
        // Sitting further out buys tangential room but costs run; a ring only fits whole where both hold.
        if (textRadius >= Math.max(hub, labelSpacingRadius(fontSize, narrowest))) return { radius: textRadius, whole: true, fontSize };
      }

      // Too narrow to hold the longest name whole — a phone always lands here. Spacing still governs:
      // pinning labels to the hub to buy run would stack them on top of each other and on the centre.
      const fontSize = fontForChars(longest, maxRun);
      const spacingFloor = Math.max(hub, labelSpacingRadius(fontSize, narrowest));
      const room = Math.max(0, live - LABEL_EDGE_PAD - LABEL_RIM_GAP - spacingFloor);
      return { radius: room >= MIN_RUN_PX ? spacingFloor : Math.max(hub, live - LABEL_EDGE_PAD - LABEL_RIM_GAP - MIN_RUN_PX), whole: false, fontSize };
    },
    [outerRadius]
  );

  const getLabelRadius = useCallback(() => labelPlanRef.current.radius || outerRadius * 0.2 + outerRadius * 0.8 * LABEL_RING_FRACTION, [outerRadius]);

  // Every named arc gets a label, clamped to the band so it may never reach past the outer arc.
  const getLabelFit = useCallback(
    (d: HierarchyRectNode) => {
      const plan = labelPlanRef.current;
      const radius = getLabelRadius();
      // A planned-whole ring skips the fit entirely: the radius was chosen so nothing needs cutting.
      if (plan.whole && d.data.name) return { fontSize: plan.fontSize, text: d.data.name, offset: 0 };
      // Fixed radius, so the text is simply cut to the run — fitArcLabel's stagger would move it.
      const run = Math.max(0, (d3Context.current.outerRadius || outerRadius) - LABEL_EDGE_PAD - LABEL_RIM_GAP - radius);
      return { fontSize: plan.fontSize, text: truncateLabel(d.data.name || "", charsFor(plan.fontSize, run)), offset: 0 };
    },
    [outerRadius, getLabelRadius]
  );

  // Every label ENDS at the rim, so a ring reads as one flush outer edge whatever the name lengths.
  // The 180° flip reverses the local x axis, which is why the anchor swaps rather than the maths.
  const getLabelTransform = useCallback(
    (d: HierarchyRectNode) => {
      const angle = (d.x0 + d.x1) / 2;
      const textAngle = (angle * 180) / Math.PI - 90;
      const flip = angle > Math.PI ? 180 : 0;
      const live = d3Context.current.outerRadius || outerRadius;
      return `rotate(${textAngle}) translate(${live - LABEL_EDGE_PAD - LABEL_RIM_GAP},0) rotate(${flip})`;
    },
    [outerRadius]
  );

  // The label runs outward from getLabelRadius to the ring edge, so that run is its budget and the
  // tangential gap to the next arc is what decides whether it can be drawn at all.

  const getTextAnchor = (d: HierarchyRectNode) => ((d.x0 + d.x1) / 2 > Math.PI ? "start" : "end");

  const renderSunburst = useCallback(
    (currentNode: HierarchyRectNode, isInitialRender: boolean = false) => {
      if (!d3Context.current.container || !d3Context.current.arc) return;

      const container = d3Context.current.container;
      // A recolour-only repaint must not re-run the orientation animations; only a change of drill node may.
      const nodeChanged = isInitialRender || d3Context.current.renderedId !== currentNode.data.id;
      d3Context.current.renderedId = currentNode.data.id as string;
      container.style("pointer-events", "none");
      const arc = d3Context.current.arc;
      const centerRadius = d3Context.current.centerRadius;
      const vizRadius = Math.min(width, height) / 2 + 50;
      const partitionLayout = d3.partition<WheelNode>().size([2 * Math.PI, vizRadius * vizRadius]);

      // Create a flag to track when exit transitions are complete
      let exitTransitionsComplete = isInitialRender;

      const hierarchy = d3
        .hierarchy(currentNode.data)
        .sum((d) => d.value || 0)
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      const root = partitionLayout(hierarchy);
      let nodes = root.children || [];

      // Undefined at the SNP ring: a SNP has no child to add, so no slice is reserved there either.
      const wedgeKind = addWedge ? addWedgeKind(currentNode.data.type) : null;
      const wedgeLabel = wedgeKind ? `+ ${kindLabel(wedgeKind)}` : undefined;
      allocateAngles(nodes, wedgeLabel ? ADD_WEDGE_ANGLE : 0);

      // — SEGMENTS —
      const segments = container.selectAll<SVGPathElement, HierarchyRectNode>("path.segment").data(nodes, (d) => d.data.id as string);

      // EXIT
      const exiting = segments.exit();
      const exitCount = exiting.size();
      let exitedCount = 0;

      if (exitCount === 0) {
        exitTransitionsComplete = true;
      }

      exiting
        .transition("exitShrink")
        .duration(isInitialRender ? 0 : motionDuration(MOTION_MS.slow))
        .ease(d3.easeCubicInOut)
        .attrTween("d", (d: any): ((t: number) => string) => {
          const interp = d3.interpolate({ x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 }, { x0: d.x0, x1: d.x1, y0: 0, y1: 0 });
          return (t: number): string => {
            return arc!({ ...d, ...interp(t) } as HierarchyRectNode) || "";
          };
        })
        .style("fill-opacity", 0)
        .on("end", () => {
          exitedCount++;
          if (exitedCount === exitCount) {
            exitTransitionsComplete = true;
          }
        })
        .remove();

      // — CENTER CIRCLE —
      container.selectAll("circle.center-circle").remove();
      const canGoBack = currentNode.parent !== null;

      const newCenterCircle = container
        .append("circle")
        .datum(currentNode)
        .attr("class", "center-circle")
        .attr("r", isInitialRender ? 0 : centerRadius)
        .attr("stroke-width", 2)
        .attr("cursor", canGoBack ? "pointer" : "default")
        .style("pointer-events", "all")
        .style("fill-opacity", isInitialRender ? 0 : 1)
        .on("click", (event) => {
          event.stopPropagation();
          if (canGoBack) backClickRef.current();
        });

      if (isInitialRender) {
        newCenterCircle.transition().duration(motionDuration(MOTION_MS.slow)).ease(d3.easeCubicInOut).attr("r", centerRadius).style("fill-opacity", 1);
      }

      // — CENTER TEXT —
      container.selectAll("text.center-text").remove();

      const newCenterText = container
        .append("text")
        .datum(currentNode)
        .attr("class", "center-text")
        .attr("text-anchor", "middle")
        .attr("pointer-events", "none")
        .attr("dy", "0.35em")
        .attr("font-size", `${CENTER_FONT_PX}px`)
        .text((currentNode.data.name ?? "").toUpperCase())
        .each(function () {
          applyCentreTitle(this as SVGTextElement, currentNode.data.name, centerRadius * 2 * CENTRE_TITLE_FIT, CENTER_FONT_PX);
        })
        .style("fill-opacity", isInitialRender ? 0 : 1);

      if (isInitialRender) {
        newCenterText.transition().duration(motionDuration(MOTION_MS.slow)).ease(d3.easeCubicInOut).style("fill-opacity", 1);
      } else if (nodeChanged) {
        newCenterText.style("fill-opacity", 0).transition().duration(motionDuration(MOTION_MS.normal)).style("fill-opacity", 1);
      }

      // ENTER
      const enter = segments
        .enter()
        .append("path")
        .attr("class", "segment")
        .attr("fill", (d) => getNodeColor(d.data, false))
        .attr("stroke-width", 1)
        .on("mouseover", (event) => {
          d3.select(event.currentTarget).transition("hoverStyle").duration(motionDuration(MOTION_MS.fast)).ease(d3.easeCubicInOut).attr("stroke-width", 2);
        })
        .on("mouseout", (event) => {
          d3.select(event.currentTarget).transition("hoverStyle").duration(motionDuration(MOTION_MS.fast)).ease(d3.easeCubicInOut).attr("stroke-width", 1);
        })
        .on("click", (event, d) => {
          event.stopPropagation();
          handleClick(d);
        });

      // Enter-only is enough: the dashboard remounts the wheel on every save (keyed on wheelRevision).
      enter.filter((d) => d.data.hiddenReason != null).attr("class", "segment is-hidden");

      // The arc is floored to a minimum angle, so its area is not the value — the number lives here.
      enter.append("title").text((d) => `${d.data.name} · ${d.value ?? 0} variants${d.data.hiddenReason ? " · hidden" : ""}`);

      const enterCount = enter.size();

      if (isInitialRender) {
        enter.attr("d", (d) => arc!(d)).style("fill-opacity", 1);
        container.style("pointer-events", "all");
      } else {
        const startEnterTransitions = () => {
          // A recolour-only repaint adds no arcs, so no enter transition would ever fire to re-enable interaction.
          if (enterCount === 0) {
            container.style("pointer-events", "all");
            return;
          }

          enter
            .attr("d", (d) => {
              const clone = Object.create(Object.getPrototypeOf(d));
              Object.assign(clone, d, { y0: 0, y1: 0 });
              return arc!(clone);
            })
            .style("fill-opacity", 0)
            .transition("enterGrow")
            .delay((_, i) => motionDuration(i * 15))
            .duration(motionDuration(MOTION_MS.slow))
            .ease(d3.easeCubicInOut)
            .attrTween("d", function (d) {
              const interp = d3.interpolate({ x0: d.x0, x1: d.x1, y0: 0, y1: 0 }, { x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 });
              return (t: number) => {
                return arc!({ ...d, ...interp(t) } as HierarchyRectNode) || "";
              };
            })
            .style("fill-opacity", 1)
            .on("end", function (_, i) {
              // i indexes the enter selection, which is shorter than nodes whenever some arcs only update.
              if (i === enterCount - 1) {
                container.style("pointer-events", "all");
              }
            });
        };

        if (exitTransitionsComplete) {
          startEnterTransitions();
        } else {
          const checkExitComplete = () => {
            if (exitTransitionsComplete) {
              startEnterTransitions();
            } else {
              setTimeout(checkExitComplete, 50);
            }
          };
          checkExitComplete();
        }
      }

      // UPDATE
      segments
        .transition("updateGrow")
        .duration(isInitialRender ? 0 : motionDuration(MOTION_MS.slow))
        .ease(d3.easeCubicInOut)
        .attrTween("d", function (d) {
          const prev = (this as any).__data__;
          const interp = d3.interpolate({ x0: prev.x0, x1: prev.x1, y0: prev.y0, y1: prev.y1 }, { x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 });
          return (t): string => {
            const { x0, x1, y0, y1 } = interp(t);
            d.x0 = x0;
            d.x1 = x1;
            d.y0 = y0;
            d.y1 = y1;
            return arc!(d) ?? "";
          };
        })
        .attr("fill", (d) => getNodeColor(d.data, false));

      // — MODIFIER BANDS —
      container.selectAll("path.wheel-modifier-band").remove();
      container.selectAll("circle.wheel-modifier-dot").remove();

      const modified = nodes.filter((d) => (d.data.modifiedCount || 0) > 0);
      // The marker sits off the node's own edge and out at the rim, so the modified arcs read as one
      // ring rather than as a highlight drawn around each segment.
      const bandArc = d3
        .arc<HierarchyRectNode>()
        .startAngle((d) => d.x0)
        .endAngle((d) => d.x1)
        .innerRadius(() => outerRadius + MODIFIER_RIM_GAP)
        .outerRadius(() => outerRadius + MODIFIER_RIM_GAP + MODIFIER_RING_PX);

      // One ring is on screen at a time, so a node's own extent already IS the right granularity:
      // whole trait at the trait ring, whole gene at the gene ring, the SNP itself at the SNP ring.
      container
        .selectAll<SVGPathElement, HierarchyRectNode>("path.wheel-modifier-band")
        .data(modified, (d) => d.data.id as string)
        .enter()
        .append("path")
        .attr("class", "wheel-modifier-band")
        .attr("d", (d) => bandArc(d));

      // The node's outer/trailing corner — its "top right" once the wedge is read in its own rotation.
      const dotRadius = outerRadius - MODIFIER_DOT_EDGE;
      const dotAngle = (d: HierarchyRectNode) => d.x1 - Math.min((d.x1 - d.x0) / 2, MODIFIER_DOT_EDGE / dotRadius);
      // No dot on a SNP: the rim arc already pinpoints it, and the dot lands on its label.
      container
        .selectAll<SVGCircleElement, HierarchyRectNode>("circle.wheel-modifier-dot")
        .data(modified.filter((d) => d.data.type !== "snp"), (d) => d.data.id as string)
        .enter()
        .append("circle")
        .attr("class", "wheel-modifier-dot")
        .attr("r", MODIFIER_DOT_PX)
        .attr("cx", (d) => Math.sin(dotAngle(d)) * dotRadius)
        .attr("cy", (d) => -Math.cos(dotAngle(d)) * dotRadius);

      // — LABELS —
      labelPlanRef.current = planRing(nodes);
      ringIndexRef.current = new Map();
      [...nodes].sort((a, b) => a.x0 - b.x0).forEach((d, i) => ringIndexRef.current.set(d.data.id, i));

      // Only an arc with no name at all goes unlabelled.
      const visible = nodes.filter((d) => getLabelFit(d).text !== "");
      const labels = container.selectAll<SVGTextElement, HierarchyRectNode>("text.node-label").data(visible, (d) => d.data.id as string);

      // EXIT
      labels
        .exit()
        .transition()
        .duration(isInitialRender ? 0 : motionDuration(MOTION_MS.normal))
        .ease(d3.easeCubicInOut)
        .style("fill-opacity", 0)
        .remove();

      // ENTER
      const enterLabels = labels
        .enter()
        .append("text")
        .attr("class", "node-label")
        .attr("transform", (d) => getLabelTransform(d))
        .attr("text-anchor", (d) => getTextAnchor(d))
        .attr("dy", ".35em")
        .attr("font-size", (d) => `${getLabelFit(d).fontSize}px`)
        .style("fill-opacity", 0)
        .text((d) => getLabelFit(d).text);

      enterLabels
        .transition()
        .delay((_, i) => motionDuration(MOTION_MS.slow + Math.min(i * 10, 200)))
        .duration(motionDuration(MOTION_MS.normal))
        .ease(d3.easeCubicInOut)
        .style("fill-opacity", 1);

      // UPDATE
      if (nodeChanged) {
        labels
          .transition()
          .duration(isInitialRender ? 0 : motionDuration(MOTION_MS.normal))
          .ease(d3.easeCubicInOut)
          .style("fill-opacity", 0)
          .transition()
          .delay(motionDuration(MOTION_MS.normal))
          .attr("transform", (d) => getLabelTransform(d))
          .attr("font-size", (d) => `${getLabelFit(d).fontSize}px`)
          .text((d) => getLabelFit(d).text)
          .transition()
          .duration(motionDuration(MOTION_MS.normal))
          .ease(d3.easeCubicInOut)
          .style("fill-opacity", 1);
      } else {
        labels
          .attr("transform", (d) => getLabelTransform(d))
          .attr("font-size", (d) => `${getLabelFit(d).fontSize}px`)
          .text((d) => getLabelFit(d).text)
          .style("fill-opacity", 1);
      }

      // — ADD WEDGE (build mode only) —
      container.selectAll("path.add-wedge").remove();
      container.selectAll("text.add-wedge-label").remove();

      if (wedgeLabel) {
        const span = { x0: -ADD_WEDGE_ANGLE / 2, x1: ADD_WEDGE_ANGLE / 2 } as HierarchyRectNode;

        container
          .append("path")
          .attr("class", "add-wedge")
          .attr("d", arc(span) ?? "")
          .style("pointer-events", "all")
          .on("click", (event: any) => {
            event.stopPropagation();
            addNodeRef.current?.(currentNode.data);
          })
          .append("title")
          .text(`Add a ${wedgeKind ? kindLabel(wedgeKind) : ""} to ${currentNode.data.name}`);

        container
          .append("text")
          .attr("class", "add-wedge-label")
          .attr("transform", getLabelTransform(span))
          .attr("text-anchor", getTextAnchor(span))
          .attr("dy", ".35em")
          .attr("font-size", `${labelPlanRef.current.fontSize}px`)
          .text(wedgeLabel);
      }

      if (isInitialRender) {
        container.style("pointer-events", "all");
        container.select("circle.center-circle").style("pointer-events", "all");
      }
    },
    [width, height, outerRadius, getLabelTransform, getLabelFit, addWedge]
  );

  const handleClick = useCallback(
    (node: HierarchyRectNode) => {
      if (!d3Context.current.root) return;
      setActiveNode(node.data);

      if (node.data.type === "trait" && onTraitClick) {
        onTraitClick(node.data.data);
      } else if (node.data.type === "gene" && onGeneClick) {
        onGeneClick(node.data.name);
      } else if (node.data.type === "snp" && onSnpClick) {
        onSnpClick(node.data.data);
      }

      // Only drill if there are children
      if (!node.children?.length) {
        return;
      }

      // Drill into children
      d3Context.current.current = node;
      setBreadcrumbs((b) => [...b, { name: node.data.name, node }]);
      renderSunburst(node);
    },
    [onTraitClick, onGeneClick, onSnpClick, renderSunburst]
  );

  const handleBackClick = useCallback(() => {
    const cur = d3Context.current.current;
    if (!cur || !d3Context.current.root) return;

    if (cur.parent) {
      if (breadcrumbs.length >= 2) {
        const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
        if (previousBreadcrumb && previousBreadcrumb.node) {
          d3Context.current.current = previousBreadcrumb.node;
          setBreadcrumbs((b) => b.slice(0, -1));
          renderSunburst(previousBreadcrumb.node);
          onNavigateUp?.(previousBreadcrumb.node.data);
        } else {
          d3Context.current.current = cur.parent;
          setBreadcrumbs((b) => b.slice(0, -1));
          renderSunburst(cur.parent);
          onNavigateUp?.(cur.parent.data);
        }
      } else {
        d3Context.current.current = d3Context.current.root;
        setBreadcrumbs([
          {
            name: d3Context.current.root.data.name,
            node: d3Context.current.root,
          },
        ]);
        renderSunburst(d3Context.current.root);
        onNavigateUp?.(d3Context.current.root.data);
      }
    }
  }, [renderSunburst, breadcrumbs, onNavigateUp]);

  backClickRef.current = handleBackClick;

  // The container transform, centre radius and arc all come from the same size. Before this they were
  // only set at init, so a resize moved the ring off-centre and drew it past the viewBox.
  const syncGeometry = useCallback(() => {
    const context = d3Context.current;
    if (!context.container) return;

    const inner = outerRadius * 0.2;
    context.centerRadius = inner;
    // Read back by the label helpers: a drill handler holds an older closure, and the props are the
    // shell's reserve arithmetic, not the measured stage — so a stale one placed labels off the ring.
    context.outerRadius = outerRadius;
    context.arc = d3
      .arc<HierarchyRectNode>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius(() => inner)
      .outerRadius(() => outerRadius);
    context.container.attr("transform", `translate(${width / 2},${height / 2})`);
  }, [width, height, outerRadius]);

  const initializeVisualization = useCallback(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const container = svg
      .append("g")
      .attr("class", "sunburst-container")
      .style("opacity", 0)
      .style("pointer-events", "none");
    d3Context.current.container = container;
    d3Context.current.initialized = true;
    syncGeometry();

    const radius = outerRadius;

    const wheelData = buildWheelData();
    if (!wheelData) return;

    const partition = d3.partition<WheelNode>().size([2 * Math.PI, radius * radius]);
    const hierarchy = d3
      .hierarchy(wheelData)
      .sum((d) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    const rootNode = partition(hierarchy);
    d3Context.current.root = rootNode;
    d3Context.current.current = rootNode;
    setBreadcrumbs([{ name: wheelData.name, node: rootNode }]);

    renderSunburst(rootNode, true);

    container
      .transition()
      .duration(motionDuration(MOTION_MS.slow))
      .ease(d3.easeCubicInOut)
      .style("opacity", 1)
      .on("end", () => {
        container.style("pointer-events", "all");
      });
  }, [width, height, outerRadius, buildWheelData, syncGeometry]);

  useLayoutEffect(() => {
    if (!svgRef.current) return;
    const data = buildWheelData();
    if (!data) {
      if (d3Context.current.initialized) {
        d3.select(svgRef.current).selectAll("*").remove();
        d3Context.current.initialized = false;
        d3Context.current.root = null;
        d3Context.current.current = null;
        d3Context.current.container = null;
        d3Context.current.renderedId = null;
        setBreadcrumbs([]);
        setActiveNode(null);
      }
      return;
    }
    if (!d3Context.current.initialized) {
      initializeVisualization();
      return;
    }
    if (d3Context.current.root?.data.id !== `panel-${selectedPanel?.id_panels}`) {
      d3.select(svgRef.current).selectAll("*").remove();
      d3Context.current.initialized = false;
      d3Context.current.renderedId = null;
      initializeVisualization();
      return;
    }
    if (d3Context.current.current) {
      syncGeometry();
      if (d3Context.current.root) syncWheelNodes(d3Context.current.root.data, data);
      renderSunburst(d3Context.current.current);

      setTimeout(() => {
        if (d3Context.current.container) {
          d3Context.current.container.select("circle.center-circle").style("pointer-events", "all");
        }
      }, 100);
    }
  }, [selectedPanel, traits, snps, geneticResults, width, height, buildWheelData, initializeVisualization, renderSunburst, syncGeometry]);

  return (
    <div ref={boxRef} className={`zoomable-sunburst-container mg-wheel mg-wheel--ink-${ink}`}>
      <div className="visualization-container">
        <svg ref={svgRef} className="mg-wheel__svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" />
      </div>
    </div>
  );
};

export default ZoomableSunburst;
